import React from 'react';
import { Link as GatsbyLink } from 'gatsby';
import {
  FormattedDate, FormattedMessage, useIntl,
} from 'react-intl';

import {
  Badge,
  DescriptionList,
  Card,
  Link,
  BadgeProps,
} from '@printdeal/ui-library';
import { messages as quoteMessages } from '../../../translations/quote';
import { Quote, QuoteStatus } from '../../../../../types/quotes/quotes';
import { colorToSemanticVariant } from '../../../../../helpers/brand';

const STATUS = {
  EXPIRED: 'expired',
};

interface QuoteListItemProps {
  quote: Quote;
  quoteStatuses: QuoteStatus[];
}

const QuoteListItem = ({
  quote,
  quoteStatuses,
}: QuoteListItemProps) => {
  const intl = useIntl();

  const {
    isExpired,
    quoteNumber,
    quoteState,
    quoteReference,
    quoteExpiryDate,
    quoteId,
  } = quote;

  const status = isExpired ? STATUS.EXPIRED : quoteState;
  const currStatus = quoteStatuses.find(
    (quoteStatus) => quoteStatus.name === status,
  );

  const badgeColor = currStatus ? colorToSemanticVariant(currStatus.color || 'blue') as BadgeProps['variant'] : 'dim';
  const quoteStateMessage = quoteMessages[status];

  return (
    <Card
      variant="horizontal"
      link={(
        <Link
          as={GatsbyLink}
          to={quoteId}
          variant="secondary"
        />
      )}
      title={(
        <div className="pd-flex pd-flex-row pd-gap-4">
          <FormattedMessage id="quotes.list.quoteNumberPrefix" defaultMessage="Quote" />
          {` ${quoteNumber}`}
          <Badge variant={badgeColor}>
            {intl.formatMessage(quoteStateMessage)}
          </Badge>
        </div>
      )}
    >
      <DescriptionList>
        <DescriptionList.Title>
          <FormattedMessage id="quotes.list.expiryDate" defaultMessage="Expiry date" />
        </DescriptionList.Title>
        <DescriptionList.Content>
          <time dateTime={quoteExpiryDate}>
            <FormattedDate value={quoteExpiryDate} year="numeric" month="long" day="numeric" />
          </time>
        </DescriptionList.Content>
        {quoteReference && (
          <>
            <DescriptionList.Title>
              <FormattedMessage id="quotes.list.reference" defaultMessage="Reference" />
            </DescriptionList.Title>
            <DescriptionList.Content>
              {quoteReference}
            </DescriptionList.Content>
          </>
        )}
      </DescriptionList>
    </Card>
  );
};

export default QuoteListItem;
